/**
 * localTemplate — loads a custom page from committed files under
 * `public/pages/<slug>/` instead of the public page API.
 *
 * Layout of a template folder:
 *   public/pages/<slug>/index.html    the page markup
 *   public/pages/<slug>/style.css     its CSS
 *   public/pages/<slug>/script.js     its JS
 *   public/pages/<slug>/config.json   everything that isn't markup
 *
 * The loaded files are shaped into the same object the API returns, so
 * PublicPageView, DynamicPageRenderer and HtmlCssSection all run unchanged —
 * the markup still ends up inside the same sandboxed iframe it would have if
 * it had arrived over the network. Nothing here widens what a template may do.
 *
 * EVERY failure returns null, and null means "use the API path". A template is
 * an optimisation: it must never be able to break a page that works today.
 *
 * Caching. nginx sets `expires 6M` on .css and .js, and files under public/
 * carry no content hash in their names, so a naive fetch could serve
 * half-year-old CSS against fresh HTML. config.json is exempt from that rule
 * (the nginx regex lists no .json), so it is revalidated on every load with
 * `cache: 'no-cache'` — one small conditional request — and its
 * `downloaded_at` is then used as a version query on the other three, which
 * therefore come from the disk cache with no request at all.
 */

const BASE = '/pages';

/**
 * True when `html` is this app's own index.html rather than a page template.
 *
 * nginx answers any unknown path with index.html at HTTP 200 (its SPA
 * fallback), so a folder that is missing index.html — a half-copied template,
 * a misspelt slug — would otherwise render the React shell inside the page
 * iframe. config.json catches most of those by failing to parse, but a folder
 * holding config.json and nothing else would slip through.
 *
 * `<div id="root">` is the shell's mount point in both the dev server and a
 * production build. Page Builder markup has no reason to carry it.
 */
function looksLikeAppShell(html) {
  return /<div\s+id=["']root["']/i.test(html);
}

/**
 * Fetch that prefers fresh but survives being offline.
 *
 * `no-cache` keeps the cached copy and revalidates it — it is not `no-store`,
 * which would re-download in full every time. The trade-off is that it needs
 * to reach the server at all, so a dead network rejects even when a perfectly
 * good copy is cached; `force-cache` is the fallback for exactly that case.
 */
async function fetchRevalidated(url) {
  try {
    return await fetch(url, { cache: 'no-cache' });
  } catch (err) {
    return fetch(url, { cache: 'force-cache' });
  }
}

async function fetchText(url) {
  const res = await fetch(url);
  if (!res.ok) return null;
  return res.text();
}

/**
 * Shape the four files into the object PublicPageView expects from the API.
 * The single section is typed `html_css_workspace` so SectionRegistry resolves
 * it to HtmlCssSection — the same component, the same sandbox.
 */
/** Anything that is not already a list becomes an empty one. */
function asList(value) {
  return Array.isArray(value) ? value : [];
}

/**
 * Anything that is not a plain object becomes an empty one.
 *
 * Same reasoning as asList: config.json is hand-edited, and these values are
 * spread or destructured downstream, where a string would silently produce
 * indexed-character junk rather than an obvious failure.
 */
function asObject(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

/**
 * Give up on the local template, and say why while developing.
 *
 * Falling back to the API silently is the right behaviour — a broken template
 * must never break a working page — but it leaves a developer with no signal
 * at all when their template does not load. The warning is development-only:
 * tests run with NODE_ENV=test and production builds stay quiet, so neither
 * gets noise from a page that simply has no local template.
 */
function bail(slug, reason) {
  if (process.env.NODE_ENV === 'development') {
    // eslint-disable-next-line no-console
    console.warn(`[localTemplate] "${slug}" falling back to the API — ${reason}`);
  }
  return null;
}

function toPageShape(slug, cfg, html, css, js) {
  const pageConfig = { ...asObject(cfg.page_config) };
  if (cfg.header) pageConfig.header = cfg.header;
  if (cfg.footer) pageConfig.footer = cfg.footer;
  pageConfig.show_header = cfg.show_header !== false;

  return {
    page_id: cfg.source_page_id ?? null,
    name: cfg.name || slug,
    slug,
    visibility: 'public',
    seo: asObject(cfg.seo),
    page_config: pageConfig,
    sections: [
      {
        id: `local-${slug}`,
        section_type: 'html_css_workspace',
        order: 0,
        is_visible: true,
        layout_config: asObject(cfg.layout_config),
        responsive_config: asObject(cfg.responsive_config),
        // Carried through when the exported page had them. A bound section
        // resolves table records at render time, so those requests still
        // happen — but dropping the binding would render the section's grid
        // empty, and a page that renders wrong is worse than a page that
        // makes a request. The page fetch itself is still saved.
        data_binding: cfg.data_binding || null,
        section_config: {
          html,
          css,
          js,
          render_mode: cfg.render_mode || 'inline',
          // Both are LISTS, and both are read straight into .forEach() /
          // .find() downstream. config.json is hand-edited, so a wrong shape
          // here is a plausible typo rather than an impossible state — coerce
          // instead of letting it throw inside the renderer.
          link_navigations: asList(cfg.link_navigations),
          cards: asList(cfg.cards ?? cfg.card_buttons),
        },
      },
    ],
  };
}

/** True when this slug is registered in localPages.js. */
export function hasLocalTemplate(slug, localPages) {
  return Array.isArray(localPages) && localPages.includes(slug);
}

/**
 * Read a template folder and return an API-shaped page, or null to fall back
 * to the API path.
 */
export async function loadLocalTemplate(slug) {
  if (!slug) return null;

  let cfg;
  try {
    const res = await fetchRevalidated(`${BASE}/${slug}/config.json`);
    if (!res.ok) return bail(slug, `config.json responded ${res.status}`);
    // A missing folder yields index.html here, which is not valid JSON, so
    // this throw is the primary guard against the SPA fallback.
    cfg = await res.json();
  } catch (err) {
    return bail(slug, `config.json is missing or is not valid JSON (${err.message})`);
  }

  if (!cfg || typeof cfg !== 'object' || Array.isArray(cfg)) {
    return bail(slug, 'config.json is not a JSON object');
  }

  // The sign-in redirect for private pages lives in the API response and has
  // no static equivalent, so anything not explicitly public goes to the API.
  if (cfg.visibility && cfg.visibility !== 'public') {
    return bail(slug, `visibility is "${cfg.visibility}" — only public pages may be local`);
  }

  const version = encodeURIComponent(String(cfg.downloaded_at || '0'));
  const q = `?v=${version}`;

  try {
    const [html, css, js] = await Promise.all([
      fetchText(`${BASE}/${slug}/index.html${q}`),
      fetchText(`${BASE}/${slug}/style.css${q}`),
      fetchText(`${BASE}/${slug}/script.js${q}`),
    ]);

    // Markup is required; styling and behaviour are optional.
    if (html == null || looksLikeAppShell(html)) {
      return bail(slug, 'index.html is missing (the SPA shell was served instead)');
    }

    return toPageShape(
      slug,
      cfg,
      html,
      css != null && !looksLikeAppShell(css) ? css : '',
      js != null && !looksLikeAppShell(js) ? js : '',
    );
  } catch (err) {
    return bail(slug, `template files could not be read (${err.message})`);
  }
}
